// A pasted Google Maps link: Share → Copy link, or the address bar. §3
export const MAPS_SHORT_HOSTS = ['maps.app.goo.gl', 'goo.gl'] as const;

export type MapsPlace = {
  readonly name: string;
  readonly latitude: number;
  readonly longitude: number;
};

export function looksLikeLink(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

// No URL here: this package carries no DOM and the API shares every line of it.
function hostOf(value: string): string {
  return (/^https?:\/\/([^/?#]+)/i.exec(value.trim())?.[1] ?? '').toLowerCase();
}

function pathOf(value: string): string {
  return /^https?:\/\/[^/?#]+([^?#]*)/i.exec(value.trim())?.[1] ?? '';
}

// Only Google's own map hosts: the server is what would follow this. §3
export function isMapsLink(value: string): boolean {
  if (!looksLikeLink(value)) return false;
  const host = hostOf(value).replace(/:\d+$/, '');
  if (MAPS_SHORT_HOSTS.includes(host as (typeof MAPS_SHORT_HOSTS)[number])) return true;
  if (host === 'maps.google.com') return true;
  return (
    /^(www\.)?google\.[a-z]{2,3}(\.[a-z]{2,3})?$/.test(host) && pathOf(value).startsWith('/maps')
  );
}

// A short link says nothing until it is followed; a long one carries it all.
export function isShortMapsLink(value: string): boolean {
  const host = hostOf(value).replace(/:\d+$/, '');
  return MAPS_SHORT_HOSTS.includes(host as (typeof MAPS_SHORT_HOSTS)[number]);
}

function pointAt(found: RegExpExecArray | null): MapsPlace | null {
  const latitude = Number(found?.[1]);
  const longitude = Number(found?.[2]);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
  return { name: '', latitude, longitude };
}

// !3d!4d is the place itself; @ is only the map's centre; then q=. §3
export function placeInMapsLink(value: string): MapsPlace | null {
  const link = value.trim();
  const found =
    pointAt(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/.exec(link)) ??
    pointAt(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/.exec(link)) ??
    pointAt(/[?&](?:q|query|ll|daddr)=(-?\d+\.?\d*),(-?\d+\.?\d*)(?:[&#]|$)/.exec(link));
  return found === null ? null : { ...found, name: nameInMapsLink(link) };
}

// /maps/place/Cavo+Nissos+Beach+Bar/@… — the venue's own name, as Google has it.
export function nameInMapsLink(value: string): string {
  const found = /\/maps\/place\/([^/@?#]+)/.exec(pathOf(value));
  if (found?.[1] === undefined) return '';
  try {
    return decodeURIComponent(found[1].replace(/\+/g, ' ')).trim();
  } catch {
    return found[1].replace(/\+/g, ' ').trim();
  }
}
